import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, TextField, Typography, Box, CircularProgress } from '@mui/material';
import { PageLayout, PageHeader } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import { loginUser, clearAuthError, selectAuthStatus, selectAuthError } from '../store/slices/authSlice';
import type { AppDispatch } from '../store/store';
import LoginIcon from '@mui/icons-material/Login';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const navigate = useNavigate();

  // Clear errors when component mounts or username/password changes
  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch, username, password]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    dispatch(clearAuthError()); // Clear previous errors on new submission
    if (!username || !password) {
      // Basic client-side validation, can be enhanced
      // For now, rely on backend or more complex form validation library
      return;
    }
    const resultAction = await dispatch(loginUser({ username, password }));
    if (loginUser.fulfilled.match(resultAction)) {
      navigate('/');
    }
    // Error handling is now primarily through useSelector for 'error' state
  };

  // Helper to get specific field error message
  const getFieldError = (fieldName: string): string | undefined => {
    if (error && typeof error === 'object' && error !== null && (error as any).errors && (error as any).errors[fieldName]) {
      return (error as any).errors[fieldName][0];
    }
    return undefined;
  };

  return (
    <PageLayout maxWidth='sm'>
      <Box className='fade-in' sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <PageHeader icon={<LoginIcon />} title='Sign in' subtitle='Welcome back to ExileGuard' />

          <EnhancedCard variant='outlined' sx={{ p: 3 }}>
            <Box component='form' onSubmit={handleSubmit} noValidate>
              <TextField
                margin='normal'
                required
                fullWidth
                id='username'
                label='Username'
                name='username'
                autoComplete='username'
                autoFocus
                value={username}
                onChange={e => setUsername(e.target.value)}
                error={!!getFieldError('username')}
                helperText={getFieldError('username')}
                disabled={status === 'loading'}
              />
              <TextField
                margin='normal'
                required
                fullWidth
                name='password'
                label='Password'
                type='password'
                id='password'
                autoComplete='current-password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                error={!!getFieldError('password')}
                helperText={getFieldError('password')}
                disabled={status === 'loading'}
              />
              {status === 'failed' && error && typeof error === 'object' && (error as any).message && !(error as any).errors && (
                <Typography color='error' variant='body2' sx={{ mt: 1 }}>
                  {(error as any).message || 'Login failed. Please try again.'}
                </Typography>
              )}
              <Button type='submit' fullWidth variant='contained' sx={{ mt: 3, mb: 2 }} disabled={status === 'loading'}>
                {status === 'loading' ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>
          </EnhancedCard>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default LoginPage;
